/* eslint-disable arrow-body-style */
/* eslint-disable @typescript-eslint/no-empty-interface */
import React, { useState, useMemo } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { useThrottleFn } from 'ahooks';
import { Button, DropdownBox, List, Popover, SearchBox, Segment } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import ItemBox from './ItemBox';
import { getOptionsByEntityTypeAndKeyword, getCascaderOptions } from './utils';

import type { IBaseProps } from '@intelligence/types/common';
import type { ITrendingGame, ITrendingGameEntityTypeData } from '../../types';
import type { IEntityTypeSegment } from './types';

import './index.scss';

interface ISearchSelectProps extends IBaseProps {
  options: ITrendingGame[];
  value: string;
  onSelect: (payload: ITrendingGame) => void;
}

const SearchSelect: React.FC<ISearchSelectProps> = (props) => {
  const { options, value, onSelect, className, style, selectedUidAndId } = props;

  const { formatMessage } = useIntl();

  const [visible, setVisible] = useState(false);
  const [entityType, setEntityType] = useState<IEntityTypeSegment>('all');
  const [keyword, setKeyword] = useState('');

  const cascaderOptions = useMemo<ITrendingGameEntityTypeData[]>(() => {
    const optionsTmp = getOptionsByEntityTypeAndKeyword(options, { entityType, keyword });
    return getCascaderOptions(optionsTmp, { keyword });
  }, [options, entityType, keyword]);

  const isNoData = !(cascaderOptions?.length > 0);

  const entityTypes = [
    { text: formatMessage({ id: 'dbi_all' }), value: 'all' },
    { text: formatMessage({ id: 'dbi_mobile' }), value: 'mobile' },
    { text: formatMessage({ id: 'dbi_pc_console_combine' }), value: 'combine' },
  ];

  const handleVisibleChange = (visible: boolean) => {
    if (visible) {
      setEntityType('all');
      setKeyword('');
    }
    setVisible(visible);
  };

  const handleSearch = (payload?: string) => {
    setKeyword(payload || '');
  };

  const { run: handleSearchRun } = useThrottleFn(handleSearch, { wait: 600 });

  const renderButtonLabel = () => {
    const length = options?.length || 0;
    const lengthText = length > 99 ? '99+' : length;
    return `${formatMessage({ id: 'dbi_all' })} (${lengthText})`;
  };

  return (
    <Popover
      trigger="click"
      placement="bottom-end"
      overlayClassName="dbi-trending-game-overlay"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      overlay={
        <DropdownBox>
          <SearchBox
            className="dbi-trending-game-search"
            placeholder={formatMessage({ id: 'dbi_search_any_game' })}
            onChange={handleSearchRun}
            onSearch={handleSearch}
          />
          <Segment
            className="dbi-trending-game-segment"
            options={entityTypes}
            value={entityType}
            onChange={(payload: string) => {
              setEntityType(payload as IEntityTypeSegment);
            }}
          />
          {isNoData && (
            <div className="dbi-trending-game-list--no-data">
              <div className="dbi-trending-game-list--no-data__inner">
                {formatMessage({ id: 'dbi_search_empty' })}
              </div>
            </div>
          )}
          {!isNoData && (
            <>
              {cascaderOptions?.length > 0 && (
                <List type="option" className="dbi-trending-game-list">
                  {cascaderOptions?.map((cascaderOption, index) => (
                    <ItemBox
                      key={index}
                      keyword={keyword}
                      data={cascaderOption}
                      value={value}
                      selectedUidAndId={selectedUidAndId}
                      onClick={(payload: ITrendingGame) => {
                        onSelect(payload);
                      }}
                    />
                  ))}
                </List>
              )}
            </>
          )}
        </DropdownBox>
      }
    >
      <Button
        style={style}
        className={classnames('dbi-trending-game-btn', className, {
          action: visible,
        })}
        type="weak"
        onClick={() => {
          handleVisibleChange(!visible);
        }}
      >
        {renderButtonLabel()}
        <SvgIcon type="arrow-down" className="dbi-trending-game-svg" />
      </Button>
    </Popover>
  );
};

export default SearchSelect;
